import { useMemo, useState } from 'react';
import { T, useGT } from 'gt-react';
import { partitionMatchableInvoiceLineItems } from '@erp/shared-utils';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { InvoiceMetadata } from '@/components/accounts-payable/invoice-detail/invoice-metadata';
import { InvoiceLineItems } from '@/components/accounts-payable/invoice-detail/invoice-line-items';
import { InvoiceChangeLog } from '@/components/accounts-payable/invoice-detail/invoice-change-log';
import { SupportingDocumentsSection } from '@/components/accounts-payable/invoice-detail/supporting-documents-section';
import { ChargesTable } from '@/components/common/charges-table';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import { useChargesForEntity } from '@/api/rpc/hooks/finance/useCharges';
import type {
  APInvoice,
  APInvoiceRelatedConversation,
  InboundEmail,
} from '@erp/rpc';
import {
  AIWorkflow,
  APInvoiceStatus,
  SystemEntity,
  OriginSyncStatus,
} from '@erp/shared-types';
import { SyncToERPButton } from '@/components/accounts-payable/invoice-detail/integrations';
import { useAcceptAPInvoice } from '@/api/rpc/hooks/accounts-payable/useAcceptAPInvoice';
import { useUnacceptAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUnacceptAPInvoice';
import { useCreatePaymentForAPInvoice } from '@/api/rpc/hooks/accounts-payable/useCreatePaymentForAPInvoice';
import { useDeleteAPInvoice } from '@/api/rpc/hooks/accounts-payable/useDeleteAPInvoice';
import { useNavigate } from '@tanstack/react-router';
import { Can } from '@/context/authz';
import { Action, Subject } from '@erp/authz';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  CalendarIcon,
  InfoIcon,
  ArrowsClockwise,
  ClockCountdown,
} from '@phosphor-icons/react';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { useUpdateAPInvoice } from '@/api/rpc/hooks/accounts-payable/useUpdateAPInvoice';
import { useBulkRerunAPInvoices } from '@/api/rpc/hooks/accounts-payable/useBulkRerunAPInvoices';
import { useBulkSnoozeRerunAPInvoices } from '@/api/rpc/hooks/accounts-payable/useBulkSnoozeRerunAPInvoices';
import { format, addDays } from 'date-fns';
import {
  TroubleDataAlert,
  EMPTY_ON_HOLD_SUMMARY,
  type OnHoldSummary,
} from '@/components/common/TroubleDataBadges';
// AP Invoices Demo (delete me) — early-pay approval banner + 3-way match entry +
// logistics cross-link card + the procurement demo's contract check; also hides
// dev-only actions while the demo is on.
import { EarlyPayApprovalBanner } from '@/demo/ap/EarlyPayApprovalBanner';
import { ContractCheckSection } from '@/demo/procurement/ap-bridge/ContractCheckSection';
import { ThreeWayMatchButton } from '@/demo/ap/ThreeWayMatchButton';
import { LogisticsLinkCard } from '@/demo/ap/LogisticsLinkCard';
import { SupplierInvoiceHistorySection } from '@/demo/ap/SupplierInvoiceHistorySection';
import { isApDemoMode } from '@/demo/ap/demo-mode';

export interface InvoiceDetailsProps {
  invoice: APInvoice;
  inboundEmail?: InboundEmail | null;
  lineItems?: Array<{
    id: string;
    lineNumber: number;
    description?: string | null;
    quantity?: string | number | null;
    unitPrice?: string | null;
    totalPrice?: string | null;
  }>;
  /**
   * Every email conversation that contributed to this invoice — the original
   * source thread plus any later threads whose extraction triggered an
   * `arbitrateAPInvoiceUpdate` run. When multiple conversations are present
   * the right-side panel renders them stacked.
   */
  relatedConversations?: APInvoiceRelatedConversation[];
}

export function InvoiceDetails({
  invoice,
  inboundEmail,
  lineItems,
  relatedConversations = [],
}: InvoiceDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'invoice-details-panel-size',
    {
      defaultValue: 66,
      initializeWithValue: true,
    },
  );

  const navigate = useNavigate();

  // Fetch charges for the invoice (only header charges)
  const { charges: allCharges } = useChargesForEntity({
    entityType: SystemEntity.AP_INVOICE,
    entityId: invoice.id,
    includeLineCharges: false,
  });

  // Filter to only header charges (no docLineId)
  const headerCharges = useMemo(
    () => allCharges.filter((charge) => !charge.docLineId),
    [allCharges],
  );

  const acceptInvoice = useAcceptAPInvoice();
  const unacceptInvoice = useUnacceptAPInvoice();
  const createPayment = useCreatePaymentForAPInvoice();

  const deleteInvoice = useDeleteAPInvoice({
    onSuccess: (data) => {
      if (data.success) {
        navigate({ to: '..', viewTransition: true });
      }
    },
  });

  const updateInvoice = useUpdateAPInvoice();
  const bulkRerun = useBulkRerunAPInvoices();
  const bulkSnoozeRerun = useBulkSnoozeRerunAPInvoices();

  const [onHoldSummary, setOnHoldSummary] = useState<OnHoldSummary>(
    EMPTY_ON_HOLD_SUMMARY,
  );
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [confirmUnacceptOpen, setConfirmUnacceptOpen] = useState(false);
  const [confirmCreatePaymentOpen, setConfirmCreatePaymentOpen] =
    useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmRerunOpen, setConfirmRerunOpen] = useState(false);
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState<Date | undefined>(
    addDays(new Date(), 1),
  );

  // Editable scheduled date for the approval confirmation dialog
  // Defaults to scheduled payment date, then invoice date
  const defaultConfirmDate = useMemo(() => {
    if (invoice.scheduledPaymentDate) {
      return new Date(invoice.scheduledPaymentDate);
    }
    return parseDateOnly(invoice.invoiceDate) ?? new Date();
  }, [invoice.scheduledPaymentDate, invoice.invoiceDate]);

  const [confirmScheduledDate, setConfirmScheduledDate] = useState<
    Date | undefined
  >(defaultConfirmDate);

  // Informational rows (qty=0 AND unitPrice=0 — e.g. seal numbers, container
  // refs, notes) are hidden by default since they can't match anything and
  // just add noise. The user can opt-in to seeing them via the toggle below.
  // Uses the same predicate the backend matcher and match-summary aggregate
  // use — see `packages/shared-utils/src/invoice-line-items.ts`.
  const [showMetadataRows, setShowMetadataRows] = useState(false);
  const { matchableLineItems, informationalLineItems } = useMemo(() => {
    const { matchable, informational } = partitionMatchableInvoiceLineItems(
      lineItems ?? [],
    );
    return {
      matchableLineItems: matchable,
      informationalLineItems: informational,
    };
  }, [lineItems]);
  // Memoize so the reference is stable across renders — `InvoiceLineItems`
  // has a `useEffect` that calls back up via `onHoldSummary`, so a fresh
  // array on every render would loop infinitely.
  const displayLineItems = useMemo(
    () =>
      showMetadataRows
        ? [...matchableLineItems, ...informationalLineItems]
        : matchableLineItems,
    [showMetadataRows, matchableLineItems, informationalLineItems],
  );

  const isDraft = invoice.status === APInvoiceStatus.DRAFT;
  const isMatched = invoice.status === APInvoiceStatus.MATCHED;
  const isAccepted = invoice.status === APInvoiceStatus.ACCEPTED;
  const isFinalized = invoice.status === APInvoiceStatus.FINALIZED;

  const hasLineItems = matchableLineItems.length > 0;
  const hasSupplier = !!invoice.supplierId;
  const canAccept = (isDraft || isMatched) && hasLineItems && hasSupplier;
  const shouldShowAcceptButton =
    (isDraft || isMatched) && !isFinalized && !isAccepted;
  const shouldShowCreatePaymentButton = isAccepted;
  const shouldShowUnacceptButton = isAccepted && !isFinalized;

  const matchSummary = invoice.lineItemMatchSummary;
  const matchTotal = matchSummary?.total ?? 0;
  const matchMatched = matchSummary?.matched ?? 0;

  const hasMissingMatches =
    matchTotal > 0 ? matchMatched < matchTotal : !isMatched;

  // Reverting an accepted invoice lands it back in MATCHED when every matchable
  // line still has a match, otherwise DRAFT — mirror the backend re-derivation
  // so the button names the state the invoice will return to.
  const revertTargetState =
    matchTotal > 0 && !hasMissingMatches
      ? APInvoiceStatus.MATCHED
      : APInvoiceStatus.DRAFT;

  // Delete button visibility and state
  const isVoided = invoice.status === APInvoiceStatus.VOID;
  const isSyncedToFamous =
    invoice.originSyncStatus === OriginSyncStatus.Success &&
    invoice.originSystem === 'famous';
  const canDelete = (isDraft || isMatched || isAccepted) && !isSyncedToFamous;
  const shouldShowDeleteButton = !isFinalized && !isVoided;

  const gt = useGT();
  const deleteTooltip = useMemo(() => {
    if (canDelete) return undefined;
    if (isSyncedToFamous)
      return gt('Cannot delete an invoice synced to Famous');
    if (isFinalized) return gt('Cannot delete a finalized invoice');
    if (isVoided) return gt('Cannot delete a voided invoice');
    if (!isDraft && !isMatched && !isAccepted)
      return gt('Only draft, matched, or accepted invoices can be deleted');
    return undefined;
  }, [
    canDelete,
    isSyncedToFamous,
    isAccepted,
    isFinalized,
    isVoided,
    isDraft,
    isMatched,
    gt,
  ]);

  function handleDelete() {
    if (!canDelete || deleteInvoice.isPending) return;
    setConfirmDeleteOpen(true);
  }

  function handleConfirmDelete() {
    if (!canDelete || deleteInvoice.isPending) return;
    setConfirmDeleteOpen(false);
    deleteInvoice.mutate({ id: invoice.id });
  }

  const acceptTooltip = useMemo(() => {
    if (canAccept) return undefined;
    const missing: string[] = [];
    if (!hasSupplier) missing.push(gt('a supplier'));
    if (!hasLineItems) missing.push(gt('at least one line item'));
    if (missing.length === 0) return undefined;
    return gt('Invoice must have {items} to be accepted', {
      items: missing.join(` ${gt('and')} `),
    });
  }, [canAccept, hasSupplier, hasLineItems, gt]);

  const confirmAcceptMessage = useMemo(() => {
    const messages: string[] = [];

    if (matchTotal > 0 && matchMatched < matchTotal) {
      messages.push(
        gt('This invoice has {matched} of {total} line items matched.', {
          matched: String(matchMatched),
          total: String(matchTotal),
        }),
      );
    } else if (matchTotal === 0 && !isMatched) {
      messages.push(gt('This invoice is not marked as matched.'));
    }

    if (onHoldSummary.onHoldCount > 0) {
      messages.push(
        gt('{count} matched line(s) are on hold.', {
          count: String(onHoldSummary.onHoldCount),
        }),
      );
    }

    return messages.length > 0
      ? messages.join(' ') + ` ${gt('Accept anyway?')}`
      : null;
  }, [matchMatched, matchTotal, isMatched, onHoldSummary, gt]);

  function handleAccept() {
    if (!canAccept || acceptInvoice.isPending) return;
    setConfirmAcceptOpen(true);
  }

  function handleConfirmAccept() {
    if (!canAccept || acceptInvoice.isPending) return;
    setConfirmAcceptOpen(false);
    acceptInvoice.mutate({ id: invoice.id });
  }

  const revertTargetLabel =
    revertTargetState === APInvoiceStatus.MATCHED ? gt('Matched') : gt('Draft');

  function handleUnaccept() {
    if (!shouldShowUnacceptButton || unacceptInvoice.isPending) return;
    setConfirmUnacceptOpen(true);
  }

  function handleConfirmUnaccept() {
    if (!shouldShowUnacceptButton || unacceptInvoice.isPending) return;
    setConfirmUnacceptOpen(false);
    unacceptInvoice.mutate({ id: invoice.id });
  }

  function handleCreatePayment() {
    if (!shouldShowCreatePaymentButton || createPayment.isPending) return;
    setConfirmScheduledDate(defaultConfirmDate);
    setConfirmCreatePaymentOpen(true);
  }

  async function handleConfirmCreatePayment() {
    if (createPayment.isPending || updateInvoice.isPending) return;

    const existingDateStr = invoice.scheduledPaymentDate?.slice(0, 10) ?? null;
    const newDateStr = confirmScheduledDate
      ? format(confirmScheduledDate, 'yyyy-MM-dd')
      : null;
    const dateChanged = existingDateStr !== newDateStr;

    if (dateChanged && confirmScheduledDate) {
      try {
        await updateInvoice.mutateAsync({
          id: invoice.id,
          scheduledPaymentDate: confirmScheduledDate.toISOString(),
          scheduledPaymentExplanation: gt('Confirmed at payment creation'),
        });
      } catch {
        return;
      }
    }

    setConfirmCreatePaymentOpen(false);
    createPayment.mutate({ id: invoice.id });
  }

  // Rerun eligibility: invoice must have a source email (direct or resolved)
  const hasSourceEmail = !!inboundEmail;
  const canRerun = (isDraft || isMatched) && hasSourceEmail;

  async function handleConfirmRerun() {
    setConfirmRerunOpen(false);
    await bulkRerun.bulkRerun([invoice.id]);
  }

  async function handleConfirmSnooze() {
    if (!snoozeDate) return;
    setSnoozeDialogOpen(false);
    await bulkSnoozeRerun.bulkSnoozeRerun([invoice.id], snoozeDate);
  }

  const sourceDocumentId =
    invoice.source === 'document' ? (invoice.sourceId ?? undefined) : undefined;
  const sourceEmailId = inboundEmail?.id ?? undefined;

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <InvoiceMetadata invoice={invoice} />

          {/* AP Invoices Demo (delete me) */}
          <EarlyPayApprovalBanner invoice={invoice} />

          {/* Procurement demo (delete me) — contract check against the
              governing agreement, same findings as invoice compliance. */}
          <ContractCheckSection invoice={invoice} />

          <div className="px-1">
            <MemoryBankNotes
              staticMetadata={{
                workflow: AIWorkflow.AP_INVOICE_INGESTION,
                workflowStep: 'AP_INVOICE_INGESTION',
              }}
              entityId={invoice.id}
              entityType={SystemEntity.AP_INVOICE}
              tooltipText={gt(
                'Add notes to guide AP invoice extraction and matching for this invoice.',
              )}
            />
          </div>

          <TroubleDataAlert
            onHoldCount={onHoldSummary.onHoldCount}
            className="mx-1"
          />

          {/* Tabs for Line Items and Charges */}
          {(lineItems && lineItems.length > 0) || headerCharges.length > 0 ? (
            <>
              <Separator />
              {/* AP Invoices Demo (delete me) — 3-way match CTA, sits right
                  above the line-items table it reconciles. */}
              <ThreeWayMatchButton invoiceId={invoice.id} />
              <Tabs
                defaultValue={
                  lineItems && lineItems.length > 0 ? 'line-items' : 'charges'
                }
                className="w-full"
              >
                <TabsList className="w-full">
                  {lineItems && lineItems.length > 0 && (
                    <TabsTrigger value="line-items">
                      <T>Line Items</T> ({matchableLineItems.length})
                    </TabsTrigger>
                  )}
                  {headerCharges.length > 0 && (
                    <TabsTrigger value="charges">
                      <T>Charges</T> ({headerCharges.length})
                    </TabsTrigger>
                  )}
                </TabsList>
                {lineItems && lineItems.length > 0 && (
                  <TabsContent value="line-items" className="mt-0">
                    {informationalLineItems.length > 0 && (
                      <div className="flex items-center justify-end gap-2 px-3 pb-2">
                        <Switch
                          id="show-metadata-rows"
                          checked={showMetadataRows}
                          onCheckedChange={setShowMetadataRows}
                        />
                        <Label
                          htmlFor="show-metadata-rows"
                          className="text-xs text-muted-foreground font-normal cursor-pointer"
                        >
                          <T context="toggle label above the AP invoice line-items table; reveals rows with zero quantity and zero unit price (e.g. seal numbers, container refs, notes)">
                            Show metadata rows
                          </T>{' '}
                          ({informationalLineItems.length})
                        </Label>
                      </div>
                    )}
                    <InvoiceLineItems
                      invoiceId={invoice.id}
                      lineItems={displayLineItems}
                      currency={invoice.currency}
                      canEditMatches={isDraft || isMatched}
                      onHoldSummary={setOnHoldSummary}
                    />
                  </TabsContent>
                )}
                {headerCharges.length > 0 && (
                  <TabsContent value="charges" className="mt-0">
                    <div className="px-3">
                      <ChargesTable
                        charges={headerCharges}
                        currency={invoice.currency ?? 'USD'}
                      />
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            </>
          ) : null}

          <SupportingDocumentsSection apInvoiceId={invoice.id} />

          {/* AP Invoices Demo (delete me) — product ↔ freight cross-link */}
          <LogisticsLinkCard invoiceId={invoice.id} />

          <InvoiceChangeLog apInvoiceId={invoice.id} />

          {/* AP Invoices Demo (delete me) — Nami's "last invoices from this
              supplier" appends a trend chart + table here and scrolls to it. */}
          <SupplierInvoiceHistorySection />
        </div>

        {/* Action buttons at the bottom */}
        <div className="shrink-0 border-t bg-background pt-3 pb-1 space-y-2">
          {/* ERP Integration Sync Button - conditionally renders based on enabled integrations */}
          <SyncToERPButton invoice={invoice} />

          {/* Rerun / Snooze Rerun actions */}
          {/* AP Invoices Demo (delete me) — hide dev-only rerun actions in demo */}
          {canRerun && !isApDemoMode() && (
            <Can I={Action.Update} a={Subject.APInvoice}>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="default"
                  disabled={bulkRerun.isLoading}
                  onClick={() => setConfirmRerunOpen(true)}
                  className="flex-1"
                >
                  <ArrowsClockwise className="mr-1.5 h-4 w-4" />
                  {bulkRerun.isLoading ? <T>Rerunning...</T> : <T>Rerun</T>}
                </Button>
                <Button
                  variant="outline"
                  size="default"
                  disabled={bulkSnoozeRerun.isLoading}
                  onClick={() => {
                    setSnoozeDate(addDays(new Date(), 1));
                    setSnoozeDialogOpen(true);
                  }}
                  className="flex-1"
                >
                  <ClockCountdown className="mr-1.5 h-4 w-4" />
                  {bulkSnoozeRerun.isLoading ? (
                    <T>Scheduling...</T>
                  ) : (
                    <T>Snooze Rerun</T>
                  )}
                </Button>
              </div>
            </Can>
          )}

          <ConfirmDialog
            open={confirmRerunOpen}
            onClose={() => setConfirmRerunOpen(false)}
            onConfirm={handleConfirmRerun}
            title={gt('Rerun Invoice Workflow?')}
            message={gt(
              'This will start a brand-new ingestion workflow run for this invoice from the source email. The existing invoice will remain until the new run completes.',
            )}
            confirmText={gt('Rerun Now')}
            cancelText={gt('Cancel')}
            loading={bulkRerun.isLoading}
          />

          <ConfirmDialog
            open={snoozeDialogOpen}
            onClose={() => setSnoozeDialogOpen(false)}
            onConfirm={handleConfirmSnooze}
            title={gt('Schedule Rerun')}
            confirmText={gt('Schedule Rerun')}
            cancelText={gt('Cancel')}
            loading={bulkSnoozeRerun.isLoading}
            disabled={!snoozeDate}
          >
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <T>
                  Choose when to rerun the ingestion workflow for this invoice.
                </T>
              </p>
              <div className="rounded-md border p-3 bg-muted/50">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <CalendarIcon className="h-4 w-4" />
                  <T>Scheduled Date</T>
                </div>
                <DatePicker
                  value={snoozeDate}
                  onSelect={setSnoozeDate}
                  placeholder={gt('Select date')}
                  fromDate={addDays(new Date(), 1)}
                />
              </div>
            </div>
          </ConfirmDialog>

          <Can I={Action.Approve} a={Subject.APInvoice}>
            {shouldShowAcceptButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="default"
                      size="default"
                      disabled={!canAccept || acceptInvoice.isPending}
                      onClick={handleAccept}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {acceptInvoice.isPending ? (
                        <T>Accepting...</T>
                      ) : (
                        <T>Accept Matches</T>
                      )}
                    </Button>
                  </div>
                </TooltipTrigger>
                {acceptTooltip && (
                  <TooltipContent>
                    <p>{acceptTooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </Can>

          <ConfirmDialog
            open={confirmAcceptOpen}
            onClose={() => setConfirmAcceptOpen(false)}
            onConfirm={handleConfirmAccept}
            title={gt('Confirm Match Acceptance')}
            message={confirmAcceptMessage ?? undefined}
            confirmText={gt('Accept Matches')}
            cancelText={gt('Cancel')}
            loading={acceptInvoice.isPending}
          />

          {shouldShowUnacceptButton && (
            <Can I={Action.Approve} a={Subject.APInvoice}>
              <Button
                variant="outline"
                size="default"
                disabled={unacceptInvoice.isPending}
                onClick={handleUnaccept}
                className="w-full"
              >
                {unacceptInvoice.isPending ? (
                  <T>Reverting...</T>
                ) : (
                  gt('Revert to {state}', { state: revertTargetLabel })
                )}
              </Button>
            </Can>
          )}

          <ConfirmDialog
            open={confirmUnacceptOpen}
            onClose={() => setConfirmUnacceptOpen(false)}
            onConfirm={handleConfirmUnaccept}
            title={gt('Revert to {state}?', { state: revertTargetLabel })}
            message={gt(
              'This returns the invoice to {state} so its matches can be edited again. The acceptance will be cleared.',
              { state: revertTargetLabel },
            )}
            confirmText={gt('Revert to {state}', { state: revertTargetLabel })}
            cancelText={gt('Cancel')}
            loading={unacceptInvoice.isPending}
          />

          {shouldShowCreatePaymentButton && (
            <Can I={Action.Approve} a={Subject.APInvoice}>
              <Button
                variant="default"
                size="default"
                disabled={createPayment.isPending}
                onClick={handleCreatePayment}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {createPayment.isPending ? (
                  <T>Creating Payment...</T>
                ) : (
                  <T>Create Payment</T>
                )}
              </Button>
            </Can>
          )}

          <ConfirmDialog
            open={confirmCreatePaymentOpen}
            onClose={() => setConfirmCreatePaymentOpen(false)}
            onConfirm={handleConfirmCreatePayment}
            title={gt('Create Payment')}
            confirmText={gt('Create Payment')}
            cancelText={gt('Cancel')}
            loading={createPayment.isPending || updateInvoice.isPending}
          >
            <div className="space-y-3">
              <TroubleDataAlert onHoldCount={onHoldSummary.onHoldCount} />
              <div className="rounded-md border p-3 bg-muted/50">
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <CalendarIcon className="h-4 w-4" />
                  <T>Scheduled Payment Date</T>
                </div>
                <DatePicker
                  value={confirmScheduledDate}
                  onSelect={setConfirmScheduledDate}
                  placeholder={gt('Select scheduled date')}
                />
                {invoice.scheduledPaymentExplanation && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1 mb-1">
                      <InfoIcon className="h-3 w-3" />
                      <T>Calculation Details</T>
                    </div>
                    <pre className="whitespace-pre-wrap font-sans">
                      {invoice.scheduledPaymentExplanation}
                    </pre>
                  </div>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                <T>A payment will be scheduled for this date.</T>
              </p>
            </div>
          </ConfirmDialog>

          <Can I={Action.Delete} a={Subject.APInvoice}>
            {/* AP Invoices Demo (delete me) — hide destructive delete in demo */}
            {shouldShowDeleteButton && !isApDemoMode() && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      variant="destructive"
                      size="default"
                      disabled={!canDelete || deleteInvoice.isPending}
                      onClick={handleDelete}
                      className="w-full"
                    >
                      {deleteInvoice.isPending ? (
                        <T>Deleting...</T>
                      ) : (
                        <T>Delete Invoice</T>
                      )}
                    </Button>
                  </div>
                </TooltipTrigger>
                {deleteTooltip && (
                  <TooltipContent>
                    <p>{deleteTooltip}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </Can>

          <ConfirmDialog
            open={confirmDeleteOpen}
            onClose={() => setConfirmDeleteOpen(false)}
            onConfirm={handleConfirmDelete}
            title={gt('Delete Invoice?')}
            message={gt(
              'This will permanently delete the invoice and all associated data. This action cannot be undone.',
            )}
            confirmText={gt('Delete Invoice')}
            cancelText={gt('Cancel')}
            loading={deleteInvoice.isPending}
          />
        </div>
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        <InvoiceEmailPanel
          inboundEmail={inboundEmail}
          relatedConversations={relatedConversations}
          sourceDocumentId={sourceDocumentId}
          sourceEmailId={sourceEmailId}
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

/**
 * Renders the email side panel for an AP invoice.
 *
 * - 0 conversations: empty state
 * - 1 conversation: a single `SmartEmailViewer` (preserves existing behavior,
 *   including the "attachments" default tab when there's no thread).
 * - 2+ conversations: every conversation is rendered stacked, with the source
 *   thread first. Each thread is wrapped with a small header so the user can
 *   tell them apart.
 */
function InvoiceEmailPanel({
  inboundEmail,
  relatedConversations,
  sourceDocumentId,
  sourceEmailId,
}: {
  inboundEmail: InboundEmail | null | undefined;
  relatedConversations: APInvoiceRelatedConversation[];
  sourceDocumentId: string | undefined;
  sourceEmailId: string | undefined;
}) {
  const gt = useGT();
  const sortedConversations = useMemo(() => {
    // Render the source thread first; everything else in stable order.
    const sorted = [...relatedConversations];
    sorted.sort((a, b) => {
      if (a.isSource && !b.isSource) return -1;
      if (!a.isSource && b.isSource) return 1;
      return 0;
    });
    return sorted;
  }, [relatedConversations]);

  if (sortedConversations.length > 1) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="space-y-4 p-1">
          {sortedConversations.map((conversation) => (
            <div
              key={conversation.conversationId}
              className="rounded-md border bg-background"
            >
              <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {conversation.isSource
                      ? gt('Original Thread', {
                          context:
                            'label for the original email thread that created the invoice',
                        })
                      : gt('Update Thread', {
                          context:
                            'label for a subsequent email thread that updated the invoice',
                        })}
                  </div>
                  <div className="truncate text-sm font-medium">
                    {conversation.subject ||
                      gt('(No subject)', {
                        context: 'fallback when an email has no subject line',
                      })}
                  </div>
                </div>
              </div>
              <div className="p-2">
                <SmartEmailViewer
                  emailData={
                    inboundEmail &&
                    inboundEmail.id === conversation.primaryEmailId
                      ? inboundEmailToEmailData(inboundEmail)
                      : undefined
                  }
                  conversationId={conversation.conversationId}
                  defaultTab="attachments"
                  sourceDocumentId={
                    conversation.isSource ? sourceDocumentId : undefined
                  }
                  sourceEmailId={
                    conversation.isSource ? sourceEmailId : undefined
                  }
                  highlightedDocumentIds={
                    conversation.isSource && sourceDocumentId
                      ? [sourceDocumentId]
                      : undefined
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (inboundEmail) {
    return (
      <SmartEmailViewer
        emailData={inboundEmailToEmailData(inboundEmail)}
        conversationId={inboundEmail.conversationId}
        defaultTab="attachments"
        sourceDocumentId={sourceDocumentId}
        sourceEmailId={sourceEmailId}
        highlightedDocumentIds={
          sourceDocumentId ? [sourceDocumentId] : undefined
        }
      />
    );
  }

  // Exactly one recovered conversation but no eagerly-loaded inboundEmail —
  // still render the thread via SmartEmailViewer. emailData is omitted
  // because the prior `if (inboundEmail)` branch has already handled the
  // truthy case; here EmailThreadViewer fetches the messages itself from
  // conversationId.
  if (sortedConversations.length === 1) {
    const conversation = sortedConversations[0];
    return (
      <SmartEmailViewer
        conversationId={conversation.conversationId}
        defaultTab="attachments"
        sourceDocumentId={conversation.isSource ? sourceDocumentId : undefined}
        sourceEmailId={conversation.isSource ? sourceEmailId : undefined}
        highlightedDocumentIds={
          conversation.isSource && sourceDocumentId
            ? [sourceDocumentId]
            : undefined
        }
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
      <T context="empty state when no email is associated with the invoice">
        No email available
      </T>
    </div>
  );
}
